import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, DollarSign, CheckCircle, Clock, LogOut } from 'lucide-react';
import { Button } from '../components/ui/button';

const AdminPayoutsPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [paidForm, setPaidForm] = useState({ paid_reference: '', payout_note: '' });
  const [marking, setMarking] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadPayouts();
  }, [token, statusFilter, monthFilter, searchTerm]);

  const loadPayouts = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (monthFilter) params.append('month', monthFilter);
      if (searchTerm) params.append('search', searchTerm);
      
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/payouts?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setPayouts(data);
    } catch (error) {
      console.error('Load failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    setMarking(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/payouts/${selectedCase.case_id}/mark-paid`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(paidForm)
      });
      setShowModal(false);
      setPaidForm({ paid_reference: '', payout_note: '' });
      loadPayouts();
      alert('Als ausgezahlt markiert');
    } catch {
      alert('Fehler');
    } finally {
      setMarking(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (monthFilter) params.append('month', monthFilter);
      if (searchTerm) params.append('search', searchTerm);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/payouts/export.csv?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payouts_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
    } catch {
      alert('Export fehlgeschlagen');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const getStatusBadge = (status) => {
    const badges = {
      open: 'bg-gray-100 text-gray-700',
      ready: 'bg-blue-100 text-blue-700',
      paid: 'bg-brand-green/10 text-brand-green'
    };
    return badges[status] || 'bg-gray-100 text-gray-600';
  };

  const kpis = {
    readySum: payouts.filter(p => p.payout_status === 'ready').reduce((sum, p) => sum + (p.partner_provision_amount || 0), 0),
    paidSum: payouts.filter(p => p.payout_status === 'paid').reduce((sum, p) => sum + (p.partner_provision_amount || 0), 0),
    readyCount: payouts.filter(p => p.payout_status === 'ready').length,
    paidCount: payouts.filter(p => p.payout_status === 'paid').length
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Laden...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-black/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-xl font-bold text-white">Auszahlungen</h1>
          <div className="flex gap-3">
            <Button onClick={() => navigate('/admin/dashboard')} variant="outline" className="border-white/20 text-white hover:bg-white/10" size="sm">Dashboard</Button>
            <Button onClick={handleLogout} variant="outline" className="border-white/20 text-white hover:bg-white/10" size="sm">
              <LogOut className="w-4 h-4 mr-2" />Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Ready (Summe)</p>
                <p className="text-2xl font-bold text-gray-900">{kpis.readySum.toFixed(2)} EUR</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-brand-green/10 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-brand-green" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Paid (Summe)</p>
                <p className="text-2xl font-bold text-gray-900">{kpis.paidSum.toFixed(2)} EUR</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Ready (Anzahl)</p>
                <p className="text-2xl font-bold text-gray-900">{kpis.readyCount}</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-brand-green/10 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-brand-green" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Paid (Anzahl)</p>
                <p className="text-2xl font-bold text-gray-900">{kpis.paidCount}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <input
              type="text"
              placeholder="Suche (Kennzeichen, Partner...)"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="flex-1 px-4 py-3 border rounded-xl"
            />
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-3 border rounded-xl">
              <option value="">Alle Status</option>
              <option value="open">Open</option>
              <option value="ready">Ready</option>
              <option value="paid">Paid</option>
            </select>
            <input type="month" value={monthFilter} onChange={e => setMonthFilter(e.target.value)} className="px-4 py-3 border rounded-xl" />
            <Button onClick={handleExport} variant="outline" className="border-2">
              <Download className="w-4 h-4 mr-2" />CSV Export
            </Button>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Kennzeichen</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Partner</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase">Reguliert</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase">Provision</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Ready At</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Paid At</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase">Aktion</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {payouts.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-gray-500">Keine Auszahlungen gefunden</td>
                </tr>
              ) : (
                payouts.map(p => (
                  <tr key={p.case_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-semibold">{p.license_plate}</td>
                    <td className="px-6 py-4 text-sm">
                      <div>{p.partner_company || '-'}</div>
                      <div className="text-xs text-gray-500">{p.partner_name}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-right">{(p.reguliert_netto || 0).toFixed(2)} EUR</td>
                    <td className="px-6 py-4 text-sm text-right">
                      <div className="font-semibold text-brand-green">{(p.partner_provision_amount || 0).toFixed(2)} EUR</div>
                      <div className="text-xs text-gray-500">{p.partner_provision_percent}%</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(p.payout_status)}`}>
                        {p.payout_status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {p.payout_ready_at ? new Date(p.payout_ready_at).toLocaleDateString('de-DE') : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {p.payout_paid_at ? new Date(p.payout_paid_at).toLocaleDateString('de-DE') : '-'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {p.payout_status === 'ready' && (
                        <Button onClick={() => { setSelectedCase(p); setShowModal(true); }} size="sm" className="bg-brand-green text-white">
                          Ausgezahlt
                        </Button>
                      )}
                      {p.payout_status === 'paid' && (
                        <span className="text-xs text-green-600">✓ Bezahlt</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && selectedCase && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Als ausgezahlt markieren</h2>
            <div className="space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <p className="text-sm text-gray-600">Fall</p>
                <p className="font-bold text-gray-900">{selectedCase.license_plate}</p>
                <p className="text-sm text-gray-600 mt-2">Betrag</p>
                <p className="text-2xl font-bold text-brand-green">{(selectedCase.partner_provision_amount || 0).toFixed(2)} EUR</p>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">Referenz (optional)</label>
                <input
                  type="text"
                  value={paidForm.paid_reference}
                  onChange={e => setPaidForm({...paidForm, paid_reference: e.target.value})}
                  placeholder="z.B. Überweisungs-ID"
                  className="w-full px-4 py-3 border rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">Notiz (optional)</label>
                <textarea
                  value={paidForm.payout_note}
                  onChange={e => setPaidForm({...paidForm, payout_note: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-3 border rounded-xl"
                  placeholder="Interne Notiz..."
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button onClick={handleMarkPaid} disabled={marking} className="flex-1 bg-brand-green text-white">
                  {marking ? 'Speichern...' : 'Bestätigen'}
                </Button>
                <Button onClick={() => setShowModal(false)} variant="outline" className="flex-1">Abbrechen</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPayoutsPage;
